import { useEffect, useState } from 'react';
import { CalendarClock, ExternalLink, User } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { STATUS_LABEL, STATUS_OPTIONS, STATUS_TONE, formatWhen } from '../constants';
import type { Meeting, MeetingStatus, UpdateMeetingPayload } from '../types';

interface MeetingDrawerProps {
  meeting: Meeting | null;
  /** Organizer / admin (dept) / superadmin may edit & delete. */
  canModify: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (id: string, payload: UpdateMeetingPayload) => void;
  onDelete: (meeting: Meeting) => void;
}

type DrawerForm = { title: string; status: MeetingStatus };

export function MeetingDrawer({ meeting, canModify, saving, onClose, onSave, onDelete }: MeetingDrawerProps) {
  const [form, setForm] = useState<DrawerForm | null>(null);

  useEffect(() => {
    setForm(meeting ? { title: meeting.title ?? '', status: meeting.status ?? 'scheduled' } : null);
  }, [meeting]);

  if (!meeting || !form) return null;

  const set = <K extends keyof DrawerForm>(key: K, value: DrawerForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(meeting.id, { title: form.title.trim(), status: form.status });
  };

  return (
    <Drawer open={!!meeting} onClose={onClose} title={meeting.title || 'Meeting Details'} width="w-[480px]">
      <form onSubmit={handleSave} className="flex h-full flex-col gap-5">
        {/* Status badge */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONE[meeting.status] ?? 'neutral'}>{STATUS_LABEL[meeting.status]}</Badge>
        </div>

        {/* When (read-only — not editable per backend) */}
        <div className="flex items-center gap-2 text-data-md text-on-surface-variant">
          <CalendarClock className="h-4 w-4" />
          {formatWhen(meeting.meeting_date, meeting.meeting_time)}
        </div>

        {/* Join link */}
        {meeting.link && (
          <a
            href={meeting.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-data-md font-semibold text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Join Meeting
          </a>
        )}

        {/* Editable fields — title + status only */}
        {canModify ? (
          <>
            <Input
              label="Title"
              name="drawer-meeting-title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              required
            />
            <Select
              label="Status"
              name="drawer-meeting-status"
              value={form.status}
              onChange={(e) => set('status', e.target.value as MeetingStatus)}
              options={STATUS_OPTIONS}
            />
          </>
        ) : (
          <div className="flex items-center justify-between border-b border-outline pb-2 text-data-md">
            <span className="text-body-sm text-on-surface-variant">Status</span>
            <span className="font-medium text-on-surface">{STATUS_LABEL[meeting.status]}</span>
          </div>
        )}

        {/* Organizer */}
        {meeting.organizer_name && (
          <div className="flex items-center gap-1.5 text-[12px] text-on-surface-variant">
            <User className="h-3.5 w-3.5" />
            Organized by {meeting.organizer_name}
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-outline pt-4">
          <div>
            {canModify && (
              <Button type="button" variant="danger" disabled={saving} onClick={() => onDelete(meeting)}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Close
            </Button>
            {canModify && (
              <Button type="submit" loading={saving}>
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </form>
    </Drawer>
  );
}
